import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/** Main site login for registered users (listeners). Admins use /admin/login. */
export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(
        message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')
          ? 'Cannot reach server. Check your connection or try again later.'
          : message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-amber-50 to-orange-50 pt-14">
      <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-[#6B3B18] rounded-2xl p-8 shadow-xl text-white">
          <div className="text-center mb-6">
            <img src="/logo.png" alt="Frequency Locus" className="w-16 h-16 mx-auto mb-3 rounded-full object-contain bg-white/10 p-1" />
            <h1 className="text-2xl font-bold">Sign in</h1>
            <p className="text-amber-100/90 text-sm mt-1">Frequency Locus</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-400 text-red-100 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="block text-amber-100/90 text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-white/15 border border-white/30 rounded-lg px-4 py-2.5 text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="admin"
                required
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-amber-100/90 text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/15 border border-white/30 rounded-lg px-4 py-2.5 text-white placeholder-amber-200/60 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-[#6B3B18] font-semibold py-2.5 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <div className="mt-4 flex flex-col gap-2 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-amber-100/90 text-sm hover:text-white"
            >
              ← Back to app
            </button>
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-amber-100/90 text-sm hover:text-white"
            >
              Create an account
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
